/**
 * city/vegetation/tree-parts.js
 * Các thành phần cơ bản của cây: hố ga gốc cây, thân phân đoạn, cành nối 2 điểm, tán lá voxel hữu cơ
 */

import * as THREE from 'three';
import { shade } from '../builder-core.js';

class MethodsHolder {
  _treePit(tree, size, rnd) {
    const rim = this.mat('#a8a29e', { roughness: 0.9 });
    const t = 0.12,
      h = 0.16;
    this.box(tree, size, h, t, rim, 0, h / 2, (size - t) / 2);
    this.box(tree, size, h, t, rim, 0, h / 2, -(size - t) / 2);
    this.box(tree, t, h, size - 2 * t, rim, (size - t) / 2, h / 2, 0);
    this.box(tree, t, h, size - 2 * t, rim, -(size - t) / 2, h / 2, 0);
    this.box(tree, size - 2 * t, 0.1, size - 2 * t, this.mat('#5b3a1e', { roughness: 1 }), 0, 0.05, 0, null, false);
    const tufts = [0.1, 0.14, 0.18];
    for (let i = 0; i < 6; i++) {
      const s = tufts[Math.floor(rnd() * tufts.length)];
      this.box(
        tree,
        s,
        s * 0.8,
        s,
        this.mat(rnd() < 0.5 ? '#4d7c0f' : '#65a30d', { roughness: 0.9 }),
        (rnd() - 0.5) * (size - 0.45),
        0.1 + s * 0.4,
        (rnd() - 0.5) * (size - 0.45),
        null,
        false
      );
    }
  }

  // Thân cây nhiều đốt thuôn dần, nghiêng nhẹ theo `lean`; trả về toạ độ ngọn
  _trunk(tree, { height, baseW, topW, segments = 4, lean = [0, 0], color = '#6b4423', banded = false }) {
    const bark = this.mat(color, { roughness: 0.95 });
    const barkDark = this.mat(shade(color, -0.22), { roughness: 0.95 });
    const segH = height / segments;
    let x = 0,
      z = 0;
    for (let i = 0; i < segments; i++) {
      const t = segments > 1 ? i / (segments - 1) : 0;
      const w = baseW + (topW - baseW) * t;
      this.box(tree, w, segH + 0.02, w, banded && i % 2 ? barkDark : bark, x, segH * (i + 0.5), z);
      x += lean[0] * segH;
      z += lean[1] * segH;
    }
    return new THREE.Vector3(x, height, z);
  }

  // Cành / rễ / thân tre: khối dài nối 2 điểm
  _limb(parent, from, to, w, material, castShadow = true) {
    const dir = new THREE.Vector3().subVectors(to, from);
    const len = dir.length();
    const mesh = this.box(
      parent,
      w,
      len,
      w,
      material,
      (from.x + to.x) / 2,
      (from.y + to.y) / 2,
      (from.z + to.z) / 2,
      null,
      castShadow
    );
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
    return mesh;
  }

  // Tán lá voxel hữu cơ: lõi đặc + khối lập phương rải trên vỏ ellipsoid, pha nhiều sắc xanh;
  // `accent` là màu hoa / lá đổi màu, ưu tiên mọc ở nửa trên tán
  _canopy(tree, { x, y, z, rx, ry, rz, palette, count, accent = null, accentRatio = 0 }, rnd) {
    this.box(tree, rx * 1.15, ry * 1.1, rz * 1.15, this.mat(palette[0], { roughness: 0.85 }), x, y, z);
    // Khối lá co theo độ lớn tán (chậu cây nhỏ dùng khối nhỏ)
    const sc = Math.min(1, Math.max(0.35, Math.min(rx, rz) / 1.4));
    const sizes = [0.3, 0.4, 0.5].map((v) => Math.round(v * sc * 100) / 100);
    for (let i = 0; i < count; i++) {
      const u = rnd() * Math.PI * 2;
      const v = Math.acos(2 * rnd() - 1);
      const k = 0.78 + rnd() * 0.3;
      const px = Math.sin(v) * Math.cos(u) * rx * k;
      let py = Math.cos(v) * ry * k;
      const pz = Math.sin(v) * Math.sin(u) * rz * k;
      if (py < -ry * 0.55) py = -ry * 0.55 + rnd() * 0.1; // đáy tán phẳng như cây thật
      const s = sizes[Math.floor(rnd() * sizes.length)];
      const accentChance = accentRatio * (py > 0 ? 1.5 : 0.5);
      const color =
        accent && rnd() < accentChance
          ? accent[Math.floor(rnd() * accent.length)]
          : palette[Math.floor(rnd() * palette.length)];
      this.box(tree, s, s, s, this.mat(color, { roughness: 0.85 }), x + px, y + py, z + pz);
    }
  }
}

export function registerTreePartsMethods(proto) {
  Object.getOwnPropertyNames(MethodsHolder.prototype).forEach((name) => {
    if (name !== 'constructor') {
      proto[name] = MethodsHolder.prototype[name];
    }
  });
}
